import React, { Suspense } from 'react'
import OrderBookTable from './OrderBookTable'
import OrderBookTopWidget from './OrderBookTopWidget'
import OrderBookBottomWidget from './OrderBookBottomWidget'
import { View } from '../../../../../styles/reactnative'
import { Skeleton } from 'antd'
import styled from 'styled-components'
import { ThemeContext } from 'theme/ThemeProvider'

const OrderBook = ({ parentHeight, parentWidth, newwindow = false }: any) => {
	const [loading, setLoading] = React.useState(true)
	const [fadeOut, setFadeOut] = React.useState(false)
	const { theme, isDarkMode } = React.useContext(ThemeContext)

	// 컴포넌트 로드 완료 후 데이터 준비 대기
	const handleDataReady = React.useCallback(() => {
		// 페이드아웃 시작
		setFadeOut(true)
		// 페이드아웃 애니메이션 완료 후 스켈레톤 제거
	}, [])

	React.useEffect(() => {
		// 최대 대기 시간 설정 (3초)
		const maxWaitTimer = setTimeout(() => {
			setFadeOut(true)
			setTimeout(() => {
				setLoading(false)
			}, 200)
		}, 200)

		return () => clearTimeout(maxWaitTimer)
	}, [])

	const loadComponent = () => {
		return (
			<OrderBookSkeletonContainer isDarkMode={isDarkMode} fadeOut={fadeOut}>
				{/* Header Skeleton */}
				<SkeletonHeader>
					<Skeleton.Button active size="small" style={{ width: 80, height: 24 }} />
					<Skeleton.Button active size="small" style={{ width: 100, height: 24 }} />
				</SkeletonHeader>

				{/* OrderBook Rows Skeleton */}
				<SkeletonContent>
					{/* Asks (매도) - 빨간색 계열 */}
					<OrderBookSection>
						{[...Array(10)].map((_, i) => (
							<SkeletonRow key={`ask-${i}`} type="ask" isDarkMode={isDarkMode} delay={i * 0.05}>
								<Skeleton.Input active size="small" style={{ width: '30%', height: 20 }} />
								<Skeleton.Input active size="small" style={{ width: '35%', height: 20 }} />
								<Skeleton.Input active size="small" style={{ width: '30%', height: 20 }} />
							</SkeletonRow>
						))}
					</OrderBookSection>

					{/* Current Price */}
					<CurrentPriceSkeleton isDarkMode={isDarkMode}>
						<Skeleton.Input active size="large" style={{ width: '60%', height: 32 }} />
					</CurrentPriceSkeleton>

					{/* Bids (매수) - 파란색 계열 */}
					<OrderBookSection>
						{[...Array(10)].map((_, i) => (
							<SkeletonRow key={`bid-${i}`} type="bid" isDarkMode={isDarkMode} delay={i * 0.05}>
								<Skeleton.Input active size="small" style={{ width: '30%', height: 20 }} />
								<Skeleton.Input active size="small" style={{ width: '35%', height: 20 }} />
								<Skeleton.Input active size="small" style={{ width: '30%', height: 20 }} />
							</SkeletonRow>
						))}
					</OrderBookSection>
				</SkeletonContent>

				{/* Bottom Widget Skeleton */}
				<SkeletonFooter>
					<Skeleton.Button active size="small" style={{ width: 120, height: 28 }} />
					<Skeleton.Button active size="small" style={{ width: 120, height: 28 }} />
				</SkeletonFooter>
			</OrderBookSkeletonContainer>
		)
	}

	if (loading) return loadComponent()

	const OrderBookLazy = React.lazy(() => import('./OrderBookTable'))

	return (
		<Suspense fallback={loadComponent()}>
			<View
				className={'ordersmall'}
				style={{
					height: parentHeight - (newwindow ? 0 : 90),
					display: 'flex',
					alignItems: 'flex-start',
					width: '100%',
					justifyContent: 'center',
					overflow: 'hidden',
					flexDirection: 'column',
				}}>
				{loading ? (
					loadComponent()
				) : (
					<>
						<OrderBookTopWidget
							parentWidth={parentWidth}
							newwindow={newwindow}
						/>
						<OrderBookLazy
							newwindow={newwindow}
							parentHeight={parentHeight}
							parentWidth={parentWidth}
							onDataReady={handleDataReady}
						/>
					</>
				)}

				<OrderBookBottomWidget newwindow={newwindow} />
			</View>
		</Suspense>
	)
}

// Styled Components for Skeleton
const OrderBookSkeletonContainer = styled.div<{ isDarkMode?: boolean; fadeOut?: boolean }>`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	background: ${props => props.theme.COLORS.background};
	padding: 12px;
	overflow: hidden;
	opacity: ${(props: { fadeOut?: boolean }) => props.fadeOut ? 0 : 1};
	transition: opacity 0.4s ease-out;
`

const SkeletonHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px;
	margin-bottom: 12px;
`

const SkeletonContent = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`

const OrderBookSection = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 4px 0;
`

const SkeletonRow = styled.div<{ type: 'ask' | 'bid', isDarkMode?: boolean, delay?: number }>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 6px 12px;
	gap: 8px;
	border-radius: 4px;
	background: ${props => {
		if (props.type === 'ask') {
			return !props.isDarkMode ? 'rgba(240, 85, 81, 0.05)' : 'rgba(240, 85, 81, 0.08)'
		} else {
			return !props.isDarkMode ? 'rgba(0, 126, 218, 0.05)' : 'rgba(0, 126, 218, 0.08)'
		}
	}};
	animation: fadeIn 0.5s ease-out ${props => props.delay || 0}s both;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(${props => props.type === 'ask' ? '-5px' : '5px'});
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
`

const CurrentPriceSkeleton = styled.div<{ isDarkMode?: boolean }>`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 16px;
	margin: 8px 0;
	background: ${props => !props.isDarkMode
		? 'rgba(255, 255, 255, 0.03)'
		: 'rgba(0, 0, 0, 0.1)'};
	border-top: 1px solid ${props => !props.isDarkMode
		? 'rgba(255, 255, 255, 0.1)'
		: 'rgba(0, 0, 0, 0.1)'};
	border-bottom: 1px solid ${props => !props.isDarkMode
		? 'rgba(255, 255, 255, 0.1)'
		: 'rgba(0, 0, 0, 0.1)'};
	animation: pulse 2s ease-in-out infinite;

	@keyframes pulse {
		0%, 100% {
			opacity: 1;
		}
		50% {
			opacity: 0.7;
		}
	}
`

const SkeletonFooter = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px;
	gap: 8px;
	margin-top: 12px;
	border-top: 1px solid ${props => props.theme.COLORS.stroke};
`

export default React.memo(OrderBook)
